import './article-info.scss';

import { concat, filter, includes, map, sortBy } from 'lodash';

import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { LegacyProduct } from '@shared/app/scripts/services/legacy-product';
import { Mask } from '@core/app/scripts/services/mask';
import { Persist } from '@core/app/scripts/services/persist';
import { Route } from '@pos/app/scripts/services/route';

/*
 * Component responsible for /article-info route.
 */
angular
  .module('posApp')
  .directive(
    'articleInfo',
    (
      $injector,
      $location: ng.ILocationService,
      displayMessage: DisplayMessage,
      legacyProduct: LegacyProduct,
      loadArticleInfo,
      mask: Mask,
      navigateTo: NavigateTo,
      persist: Persist,
      route: Route,
      translate: ng.translate.ITranslateService,
    ) => ({
      restrict: 'E',

      scope: true,

      template: require('./article-info.html').default,

      link: function ($scope: any) {
        const $translate = translate;

        $scope.param = route.param;

        // POS-1815 inject in a normal way once we load data in right order
        $scope.articleInfoInit = $injector.get('articleInfoInit');

        $scope.dataLoaded = false;
        $scope.openArticlePopup = false;
        $scope.openShopPopup = false;
        $scope.openImagePopup = false;
        $scope.openSetPopup = false;
        $scope.selectAll = false;
        $scope.filter = {};
        $scope.stockView = 'matrix';

        $scope.openColumnPopup = false;

        $scope.toggleColumnPopup = () => {
          $scope.openColumnPopup = $scope.openColumnPopup ? false : true;
        };

        $scope.persistColumns = () => {
          persist.set(
            'posArticleInfoColumns',
            JSON.stringify($scope.articleInfoColumns),
          );
        };

        if (persist.get('posArticleInfoColumns')) {
          $scope.articleInfoColumns = JSON.parse(
            persist.get('posArticleInfoColumns'),
          );
        } else {
          $scope.articleInfoColumns = {
            barcode: true,
            shop: true,
            code: true,
            artnrsupplier: true,
            description: true,
            maincolor: true,
            maincolordescription: true,
            colorFamily: true,
            subcolor: true,
            subcolordescription: true,
            suppliercolor: true,
            size: true,
            price: true,
            qty: true,
          };

          $scope.persistColumns();
        }

        const loc = $translate.instant('Branch');
        const mc = $translate.instant('Main color');
        const sc = $translate.instant('Sub color');
        const lc = $translate.instant('Supplier color');

        $scope.header = [loc, mc, sc, lc];

        $scope.exit = () => {
          //reset form
          $scope.listData = [];
          $scope.filter = {};
          $scope.articles = [];
          $scope.openShopPopup = false;
          $scope.openSetPopup = false;

          //go back to referer
          navigateTo(loadArticleInfo.pageRef);
          loadArticleInfo.pageRef = '';
        };

        $scope.stockFilter = (row) => {
          const filter = $scope.filter;
          let met = false;
          let req = false;

          for (const i in filter) {
            if (filter[i]) {
              if (i < 4) {
                if (
                  row[i].toLowerCase().search(filter[i].toLowerCase()) == -1
                ) {
                  return false;
                }
              } else {
                //sizes: at least one value required if at least one filter is active
                req = true;
                if (row[i] !== 0) {
                  met = true;
                }
              }
            }
          }
          if (req) {
            return met;
          }
          return true;
        };

        $scope.persistShops = () => {
          persist.set(
            'posArticleInfoShops',
            JSON.stringify($scope.articleInfoInit.shops),
          );
        };

        $scope.filterShops = () => {
          //Add the shops that are checked in the list to the shops filter
          $scope.shopsFilter = [];
          for (const s in $scope.articleInfoInit.shops) {
            if ($scope.articleInfoInit.shops[s].checked) {
              $scope.shopsFilter.push($scope.articleInfoInit.shops[s].id);
            }
          }
        };

        $scope.refreshGrid = () => {
          $scope.persistShops();
          $scope.filterShops();

          $scope.reloadArticleInfo();
        };

        /*
         * This method is called when an item in the Set popup is chosen
         */
        $scope.refreshGridArt = (item) => {
          $location.hash(`article-info/${item.barcode}`);
          $scope.openSetPopup = false;

          $scope.param = item.barcode;

          $scope.reloadArticleInfo();
        };

        $scope.selectAllShops = () => {
          for (const s in $scope.articleInfoInit.shops) {
            $scope.articleInfoInit.shops[s].checked = $scope.selectAll;
          }
          $scope.persistShops();
          $scope.filterShops();

          $scope.reloadArticleInfo();
        };

        /*
         * These methods open and close the popups on this page
         */
        $scope.toggleShopPopup = () => {
          $scope.openShopPopup = !$scope.openShopPopup;
        };

        $scope.toggleStockView = () => {
          if ($scope.stockView === 'matrix') {
            $scope.stockView = 'list';
          } else {
            $scope.stockView = 'matrix';
          }
        };

        $scope.toggleSetPopup = () => {
          $scope.openSetPopup = !$scope.openSetPopup;
        };

        $scope.toggleImagePopup = () => {
          $scope.openImagePopup = !$scope.openImagePopup;
        };

        $scope.reloadArticleInfo = () => {
          $scope.dataLoaded = false;
          displayMessage.showLoading();

          $scope.articles = [];
          $scope.listData = [];
          $scope.header = [loc, mc, sc, lc];
          $scope.selectedArticle = {};
          $scope.selectedLine = -1;
          $scope.sizes = [];

          legacyProduct.getByBarcode($scope.param).then(
            (stockResponse) => {
              mask.hide();

              const stockInfo = stockResponse?.stock?.rawStock[0],
                rawStock = stockResponse?.stock?.rawStock;

              if (rawStock) {
                $scope.filteredRawStock = filter(rawStock, (val) =>
                  includes($scope.shopsFilter, val.warehouseid),
                );
              }

              if (stockInfo) {
                $scope.selectedArticle.articleCode = stockInfo.code;
                $scope.selectedArticle.articleNumberSupplier =
                  stockInfo.artnrsupplier;
                $scope.selectedArticle.sizeLabel = stockInfo.size;
                $scope.selectedArticle.price = stockInfo.price;
                $scope.selectedArticle.color = stockInfo.maincolor;
                $scope.selectedArticle.brand = stockInfo.brand;
                $scope.selectedArticle.articleDescription =
                  stockInfo.description;
                $scope.selectedArticle.colorFamily = stockInfo.colorFamily;
              } else {
                // show error on load
              }

              $scope.sizes = stockResponse?.stock?.getSizes();
              $scope.header = concat($scope.header, $scope.sizes);

              const stock = stockResponse?.stock?.get(),
                filteredStock = filter(stock, (val) =>
                  includes($scope.shopsFilter, val.warehouseid),
                ),
                sortedStock = sortBy(filteredStock, [
                  'subcolor',
                  'maincolor',
                  'shop',
                ]);

              $scope.listData = map(sortedStock, (stock) => {
                return concat(
                  [
                    stock.shop,
                    `${stock.maincolor} / ${stock.colorFamily}`,
                    `${stock.subcolor} / ${stock.skus[0].subcolordescription}`,
                    stock.suppliercolor,
                  ],
                  map($scope.sizes, (size) => stock[size]),
                );
              });

              $scope.articles = stockResponse.productsInSet;

              $scope.dataLoaded = true;
            },
            () => {
              mask.hide();

              displayMessage.message($translate.instant('ThereWasAnError'));

              $scope.dataLoaded = true;
            },
          );
        };

        $scope.filter = {};
        $scope.listFilter = {};
        $scope.filterShops();
        $scope.reloadArticleInfo();

        $scope.getClass = (item) => {
          if (typeof item === 'number') {
            if (item === 0) {
              return 'stock-zero';
            }
            return 'stock-number';
          }
          return 'stock-string';
        };
      },
    }),
  );
